import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import useBasicBuilder, {
  LEFT_ID,
} from 'components/MultiColumnBuilder/useBasicBuilder';
import { v4 as uuidv4 } from 'uuid';
import { snakeToCamelCaseKeys } from 'services/helpers';

export const CUSTOM_FIELD_ITEM_ID = 'custom_field';

const selfDisabledDropZones = [LEFT_ID];

const nonHidableColumns = ['fullName', 'name', 'activityType'];

export const isNonHidebleColumn = ({ id }) => {
  return nonHidableColumns.some((columnId) => id === columnId);
};

export const useScheduledActivitiesBuilder = ({
  settings,
  defaultColumns,
  handleChange,
  displayDefaultColumns,
  orderSorting = false,
  disabledNameField = true,
  isGropedOptions = false,
  t,
}) => {
  const [showModal, setShowModal] = useState(null);
  const defaultColumnsRef = useRef(defaultColumns);
  const rightItems = useMemo(() => {
    let visibleColumns = settings.reduce(
      (acc, { label, id, visible, fieldId, objectId, ...rest }) => {
        const { descriptionVisibility, description } =
          snakeToCamelCaseKeys(rest);
        if (visible) {
          return acc.concat({
            label,
            id,
            sortable: true,
            disabled: isNonHidebleColumn({ id }),
            displayName: label,
            fieldId,
            objectId,
            description,
            descriptionVisibility: descriptionVisibility,
            visible: true,
          });
        }
        return acc;
      },
      []
    );
    if (visibleColumns.length === 0 || displayDefaultColumns) {
      visibleColumns = defaultColumnsRef.current.filter(
        ({ visible }) => visible
      );
      if (orderSorting) {
        visibleColumns = visibleColumns.sort((a, b) => a.order - b.order);
      } else {
        visibleColumns = visibleColumns.sort((a, b) =>
          a.label.localeCompare(b.label)
        );
      }
    }

    return visibleColumns.map((item) => {
      if (item.id === 'activityType') {
        return {
          ...item,
          disabled: disabledNameField,
        };
      }
      return item;
    });
  }, [settings, displayDefaultColumns, disabledNameField, orderSorting]);

  const rightItemsById = useMemo(() => {
    return rightItems.reduce((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {});
  }, [rightItems]);

  const onCustomFieldModalConfirm = useCallback((element) => {
    setShowModal({ ...element });
  }, []);

  const customFieldItem = useMemo(() => {
    return {
      label: t('New Custom Field (Drag to Create)'),
      displayName: t('New Custom Field (Drag to Create)'),
      id: uuidv4(),
      fieldId: CUSTOM_FIELD_ITEM_ID,
      isLocked: false,
    };
  }, [t]);

  const leftItems = useMemo(() => {
    return settings.reduce(
      (
        acc,
        { id, label, disabled, fieldId, objectId, isGroupedItem, ...rest }
      ) => {
        const { descriptionVisibility, description } =
          snakeToCamelCaseKeys(rest);
        if (!rightItemsById?.[id]) {
          if (isGroupedItem) {
            return acc.concat({
              label,
              displayName: label,
              id,
              isGroupedItem,
            });
          }
          return acc.concat({
            label,
            displayName: label,
            id,
            icon: '',
            sortable: true,
            disabled,
            fieldId,
            description,
            descriptionVisibility,
            objectId,
          });
        }
        return acc;
      },
      []
    );
  }, [rightItemsById, settings]);

  const leftSettingItemsById = useMemo(() => {
    return leftItems.reduce((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {});
  }, [leftItems]);

  const leftWithDefaultColumns = useMemo(() => {
    return defaultColumns.reverse().reduce((acc, { id, label }) => {
      if (!rightItemsById?.[id] && !leftSettingItemsById?.[id]) {
        acc.unshift({
          label,
          displayName: label,
          id,
          icon: '',
          sortable: true,
          disabled: isNonHidebleColumn({ id }),
        });
      }
      return acc;
    }, leftItems);
  }, [defaultColumns, leftItems, leftSettingItemsById, rightItemsById]);

  const builderProps = useBasicBuilder({
    initialLeftColumnItems: leftWithDefaultColumns,
    initialRightColumnItems: rightItems.map((item) => ({
      ...item,
      displayName: item.label,
    })),
    itemsOrdering: [customFieldItem.id].concat(settings.map((item) => item.id)),
    editable: true,
    selfDisabledDropZones,
    rightColumnItemsWithContentForceDirty: false,
    customFieldSupport: true,
  });

  useEffect(() => {
    const customFieldIndex = builderProps?.rightColumnItems.findIndex(
      ({ fieldId }) => fieldId === CUSTOM_FIELD_ITEM_ID
    );
    if (customFieldIndex >= 0) {
      setShowModal({
        index: customFieldIndex,
      });
    }
  }, [builderProps?.rightColumnItems, settings, handleChange]);

  return {
    builderProps: {
      ...builderProps,
      leftColumnProps: {
        options:
          builderProps?.leftColumnProps?.options?.[0]?.fieldId !==
          CUSTOM_FIELD_ITEM_ID
            ? [customFieldItem, ...builderProps.leftColumnProps.options]
            : builderProps.leftColumnProps.options,
        ...builderProps.leftColumnProps,
        isGroupedItem: isGropedOptions,
      },
      rightColumnItems: builderProps.rightColumnItems.map((item) => ({
        ...item,
        visible: true,
        width: item?.width ? item.width : '100px',
      })),
      leftColumnHeaderProps: {
        ...builderProps.leftColumnHeaderProps,
        zIndex: 0,
      },
      rightColumnHeaderProps: {
        ...builderProps.rightColumnHeaderProps,
        zIndex: 0,
      },
    },
    showModal,
    setShowModal,
    onCustomFieldModalConfirm,
    customFieldItem,
  };
};
